/**
 * Shared query string utilities for UI plugins.
 *
 * Converts pagination + nested filter params into the format expected by
 * backend handlers using `buildFilterWithRefs(req.query.filter, ...)`.
 *
 * Example output: `?page=1&limit=20&filter[status]=active&filter[name]=NYC`
 */

/** Generic list query params with typed filter object. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ListQueryParams<TFilter = Record<string, any>> {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  q?: string;
  filter?: TFilter;
}

/** Keys that are kept flat (not nested under `filter`). */
const FLAT_KEYS = ['page', 'limit', 'sortBy', 'sortOrder', 'q'];

/**
 * Build a URL query string from pagination + nested filter params.
 *
 * Flat keys (`page`, `limit`, `sortBy`, `sortOrder`, `q`) are appended as-is.
 * The `filter` object is recursively encoded as `filter[key]=value`.
 * Date values are serialized with `.toISOString()`.
 */
export const buildQueryString = (params?: Record<string, any>): string => {
  if (!params) return '';

  const searchParams = new URLSearchParams();

  // Handle flat params (pagination + generic search)
  FLAT_KEYS.forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value instanceof Date ? value.toISOString() : String(value));
    }
  });

  // Handle filter object
  const filter = params.filter;
  if (filter && typeof filter === 'object') {
    appendFilterParams(searchParams, filter, 'filter');
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

/**
 * Recursively append nested filter params.
 * `{ status: 'active' }` with prefix `filter` → `filter[status]=active`
 */
export function appendFilterParams(
  searchParams: URLSearchParams,
  obj: Record<string, any>,
  prefix: string,
): void {
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    const paramKey = `${prefix}[${key}]`;

    if (value instanceof Date) {
      searchParams.append(paramKey, value.toISOString());
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      appendFilterParams(searchParams, value, paramKey);
    } else {
      searchParams.append(paramKey, String(value));
    }
  });
}

/** Convenience helper for pagination-only queries (no filters). */
export const buildPaginationQuery = (params?: {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): string => buildQueryString(params);
